import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { authHeaders } from "../utils/auth";
import { BACKEND_URL } from "../utils/api";
import { usePrefill } from "../context/PrefillContext";

// Landing page for the Lextria Dashboard "Generate Form" link:
// /import#token=<hand-off jwt> (fragment: never sent to any server) or /import?token=<hand-off jwt>
function LextriaImport() {
  const navigate = useNavigate();
  const { prefill, setPrefill, clearPrefill } = usePrefill();
  const [status, setStatus] = useState("loading"); // loading | done | error
  const [error, setError] = useState(null); // {title, message}
  // The token works once; this guard stops React StrictMode's double effect from sending it twice
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // Token from the fragment (#token=) or the query (?token=); kept in memory only, never logged
    const token =
      new URLSearchParams(window.location.hash.slice(1)).get("token") ||
      new URLSearchParams(window.location.search).get("token");
    // Remove the token from the address bar (and browser history) right away
    try {
      window.history.replaceState(window.history.state, "", window.location.pathname);
    } catch (e) {}

    if (!token) {
      if (prefill) {
        setStatus("done");
      } else {
        setError({
          title: "No patent link",
          message: "Open this page from the Lextria Dashboard using the \"Generate Form\" button.",
        });
        setStatus("error");
      }
      return;
    }

    (async () => {
      try {
        const headers = await authHeaders();
        const response = await fetch(`${BACKEND_URL}/api/lextria/import`, {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          const detail = typeof body.detail === "string" ? body.detail : "";
          if (response.status === 401) {
            setError({
              title: "Link expired or already used",
              message: detail || "Click Generate Form in the Lextria Dashboard again.",
            });
          } else if (response.status === 403) {
            setError({
              title: "Not allowed",
              message: detail || "You are not allowed to send this patent to AutoIP.",
            });
          } else if (response.status === 404) {
            setError({
              title: "Patent not found",
              message: detail || "This patent no longer exists in the Lextria Dashboard.",
            });
          } else {
            setError({
              title: "Could not load patent",
              message: detail || "The Lextria Dashboard could not be reached. Please try again.",
            });
          }
          setStatus("error");
          return;
        }
        setPrefill(body);
        setStatus("done");
      } catch (e) {
        setError({
          title: "Network error",
          message: "Could not reach the AutoIP server. Check your connection and click Generate Form again.",
        });
        setStatus("error");
      }
    })();
    // Runs once per page load by design (single-use token)
  }, []);

  const names = (list) => (Array.isArray(list) ? list.map((p) => p.name).filter(Boolean) : []);

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-10 px-4">
        <div className="max-w-2xl mx-auto bg-white p-8 rounded-xl shadow-lg border border-gray-200">
          <h1 className="text-2xl md:text-3xl font-extrabold text-blue-700 mb-6 text-center">
            Import from Lextria Dashboard
          </h1>

          {status === "loading" && (
            <div className="text-center text-gray-600 py-8">Loading patent details...</div>
          )}

          {status === "error" && error && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-5">
              <h2 className="text-lg font-bold text-red-700 mb-2">{error.title}</h2>
              <p className="text-red-700">{error.message}</p>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="mt-4 bg-gray-200 text-gray-800 px-4 py-2 rounded font-semibold hover:bg-gray-300"
              >
                Go to home
              </button>
            </div>
          )}

          {status === "done" && prefill && (
            <>
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-5 space-y-3">
                <div>
                  <span className="text-sm font-semibold text-gray-600">Tracking ID: </span>
                  <span className="font-bold text-gray-900">{prefill.tracking_id || "-"}</span>
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-600">Title: </span>
                  <span className="text-gray-900">{prefill.patent_title || "-"}</span>
                </div>
                {prefill.application_no && (
                  <div>
                    <span className="text-sm font-semibold text-gray-600">Application No: </span>
                    <span className="text-gray-900">{prefill.application_no}</span>
                  </div>
                )}
                <div>
                  <span className="text-sm font-semibold text-gray-600">Applicants: </span>
                  <span className="text-gray-900">{names(prefill.applicants).join(", ") || "-"}</span>
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-600">Inventors: </span>
                  <span className="text-gray-900">{names(prefill.inventors).join(", ") || "-"}</span>
                </div>
              </div>

              <p className="text-sm text-gray-600 mt-4">
                These details will be filled into the forms you open next. You can still edit everything before generating.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => navigate("/form135")}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700"
                >
                  Forms 1/3/5/28
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/form-selector")}
                  className="flex-1 bg-white border-2 border-blue-600 text-blue-700 py-3 rounded-lg font-semibold hover:bg-blue-50"
                >
                  Choose other forms
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  clearPrefill();
                  navigate("/");
                }}
                className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Discard imported data
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default LextriaImport;
